import { apiClient, createAuthHeaders } from "../api-client";

export interface CreateThemeDto {
  name: string;
  content: string; // JSON string
  colors: string[]; // 4 hex preview colors
  isDefault?: boolean;
}

export interface UpdateThemeDto {
  name?: string;
  content?: string;
  colors?: string[];
  isDefault?: boolean;
}

/**
 * Response of GET /api/editor/projects/:id/theme.
 * `content` is a JSON string (single-encoded — parse ONCE).
 * `sourceKey` identifies the chat card (toolUseId) that performed the apply,
 * letting cards derive their "applied" state from the server (#54).
 */
export interface ProjectAppliedTheme {
  themeId: string;
  name: string;
  appliedAt: string;
  sourceKey?: string;
  content: string | null;
}

export const themesApi = {
  /**
   * List all themes (built-in + workspace custom)
   */
  async listThemes(token: string, workspaceId: string) {
    const response = await apiClient.get(
      `/api/themes/workspaces/${workspaceId}/themes`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Create a new custom theme
   */
  async createTheme(token: string, workspaceId: string, dto: CreateThemeDto) {
    const response = await apiClient.post(
      `/api/themes/workspaces/${workspaceId}/themes`,
      dto,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update a custom theme
   */
  async updateTheme(token: string, themeId: string, dto: UpdateThemeDto) {
    const response = await apiClient.put(`/api/themes/${themeId}`, dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get a single theme by ID
   */
  async getTheme(token: string, themeId: string) {
    const response = await apiClient.get(`/api/themes/${themeId}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Delete a custom theme
   */
  async deleteTheme(token: string, themeId: string) {
    const response = await apiClient.delete(`/api/themes/${themeId}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Bulk delete custom themes
   */
  async bulkDeleteThemes(token: string, ids: string[]) {
    const response = await apiClient.post(
      `/api/themes/bulk-delete`,
      { ids },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data as { deletedCount: number };
  },

  /**
   * Apply a theme to a project.
   * `mode` is forwarded to the slide thumbnail capture so regenerated previews
   * match the user's currently-viewed mode (light vs dark).
   */
  async applyTheme(
    token: string,
    projectId: string,
    themeId: string,
    mode?: "light" | "dark",
    sourceKey?: string,
  ) {
    const response = await apiClient.post(
      `/api/editor/projects/${projectId}/apply-theme`,
      { themeId, mode, ...(sourceKey ? { sourceKey } : {}) },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get the current theme applied to a project
   */
  async getProjectTheme(
    token: string,
    projectId: string,
  ): Promise<ProjectAppliedTheme | null> {
    const response = await apiClient.get(
      `/api/editor/projects/${projectId}/theme`,
      {
        headers: createAuthHeaders(token),
      },
    );
    // The endpoint returns null (empty body) when no theme is applied.
    return (response.data as ProjectAppliedTheme | null) || null;
  },

  /**
   * Remove the applied theme from a project
   */
  async removeProjectTheme(token: string, projectId: string) {
    const response = await apiClient.delete(
      `/api/editor/projects/${projectId}/theme`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },
};
